import React, { createContext, useContext, useReducer } from 'react';
import { toast } from "@/components/ui/use-toast";

interface UploadStatus {
  taskId: string;
  filename: string;
  status: 'preparing' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  processedFiles?: number;
  totalFiles?: number;
}

interface UploadState {
  uploads: Record<string, UploadStatus>;
  notifications: Array<{
    type: 'INITIALIZE' | 'PROGRESS' | 'FILE_COMPLETE' | 'COMPLETE' | 'FAIL';
    payload: any;
  }>;
}

type UploadAction =
  | { type: 'INITIALIZE_UPLOAD'; payload: { taskId: string; files: File[] } }
  | { type: 'FAIL_UPLOAD'; payload: { taskId: string; error: string } };

const initialState: UploadState = {
  uploads: {},
  notifications: []
};

const uploadReducer = (state: UploadState, action: UploadAction): UploadState => {
  switch (action.type) {
    case 'INITIALIZE_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.taskId]: {
            taskId: action.payload.taskId,
            filename: action.payload.files[0].name,
            status: 'failed',
            progress: 0,
            totalFiles: action.payload.files.length,
            processedFiles: 0,
            error: 'Upload feature coming soon'
          }
        }
      };
    case 'FAIL_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.taskId]: {
            ...state.uploads[action.payload.taskId],
            status: 'failed',
            error: action.payload.error
          }
        }
      };
    default:
      return state;
  }
};

interface UploadContextType {
  state: UploadState;
  startUpload: (files: File[], tags?: string[]) => Promise<void>;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(uploadReducer, initialState);

  // Disabled - no API calls
  const startUpload = async (files: File[], _tags?: string[]) => {
    const taskId = `upload-${Date.now()}`;

    dispatch({
      type: 'INITIALIZE_UPLOAD',
      payload: { taskId, files }
    });

    // Show coming soon message
    toast({
      title: "Upload Feature Coming Soon",
      description: `Document upload functionality is currently under development.`,
      duration: 5000,
    });

    dispatch({
      type: 'FAIL_UPLOAD',
      payload: {
        taskId,
        error: 'Upload feature coming soon'
      }
    });
  };

  return (
    <UploadContext.Provider value={{ state, startUpload }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};

export default UploadProvider;
